import type { RenderRelationshipCard } from '../../../insightRenderShapes'
import { Note } from '../InsightsBlocks'
import { InsightEntityNames } from './InsightEntityNames'
import { InsightSupportingRecords } from './InsightSupportingRecords'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../../../shopifyUiTracking'
import { useState } from 'react'
import type { RelationshipDensity } from './relationship-density'
import { ProductImageDeck } from './ProductImageDeck'
import { shopifyComponentCopy } from '@testfeed/product-language'
/** All rows supplied by the server, including directional comparisons; no hidden warehouse fetch. */
export function RelationshipDetailsModal({
  id,
  card,
  density = 'current',
}: {
  id: string
  card: RenderRelationshipCard
  density?: RelationshipDensity
}) {
  const shopify = useAppBridge()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  if (density === 'compact')
    return (
      <s-modal id={id} heading={card.title}>
        <s-stack gap="base">
          {card.rows.map((row) => {
            const expanded = expandedId === row.id
            const detailsId = `${id}-${row.id.replace(/[^a-zA-Z0-9_-]/g, '')}`
            return (
              <s-stack key={row.id} gap="small">
                <s-grid gridTemplateColumns="auto minmax(0, 1fr)" gap="small" alignItems="start">
                  <ProductImageDeck left={row.left} right={row.right} />
                  <div style={{ minInlineSize: 0, overflowWrap: 'anywhere' }}>
                    <s-stack gap="small-100">
                      <s-text fontWeight="semibold">
                        <InsightEntityNames entities={[row.left, row.right]} separator=" and " />
                      </s-text>
                      <s-text fontWeight="medium">{row.summary}</s-text>
                    </s-stack>
                  </div>
                </s-grid>
                <s-button
                  variant="tertiary"
                  aria-expanded={expanded}
                  aria-controls={detailsId}
                  onClick={() => setExpandedId(expanded ? null : row.id)}
                >
                  {expanded
                    ? shopifyComponentCopy.productPairsHideDetails
                    : shopifyComponentCopy.productPairsShowDetails}
                </s-button>
                <div id={detailsId} hidden={!expanded}>
                  <s-stack gap="small">
                    {row.details.map((detail) => (
                      <s-grid key={detail.label} gridTemplateColumns="minmax(0, 1fr) auto" gap="small">
                        <s-text>{detail.label}</s-text>
                        <s-text fontWeight="medium">{detail.value}</s-text>
                      </s-grid>
                    ))}
                    <InsightSupportingRecords evidence={row.supportingRecords} />
                  </s-stack>
                </div>
                <s-divider />
              </s-stack>
            )
          })}
          {card.rows[0] ? <Note>{card.rows[0].note}</Note> : null}
          <Note>{card.footer}</Note>
        </s-stack>
        <s-button
          slot="secondary-actions"
          commandFor={id}
          command="--hide"
          onClick={() => trackShopifyAction(shopify, 'close-patterns', () => undefined)}
        >
          Close
        </s-button>
      </s-modal>
    )
  return (
    <s-modal id={id} heading={card.title}>
      <s-stack gap="base">
        {card.rows.map((row) => (
          <s-stack key={row.id} gap="small">
            <s-text fontWeight="semibold">
              <InsightEntityNames entities={[row.left, row.right]} separator=" and " />
            </s-text>
            {row.details.map((detail) => (
              <s-grid key={detail.label} gridTemplateColumns="minmax(0, 1fr) auto" gap="small">
                <s-text>{detail.label}</s-text>
                <s-text fontWeight="medium">{detail.value}</s-text>
              </s-grid>
            ))}
            <Note>{row.note}</Note>
            <InsightSupportingRecords evidence={row.supportingRecords} />
            <s-divider />
          </s-stack>
        ))}
        <Note>{card.footer}</Note>
      </s-stack>
      <s-button
        slot="secondary-actions"
        commandFor={id}
        command="--hide"
        onClick={() => trackShopifyAction(shopify, 'close-patterns', () => undefined)}
      >
        Close
      </s-button>
    </s-modal>
  )
}
